/**
 * Finds out whether a video plane the viewer treats as an alpha WebM really has transparent
 * pixels, by drawing a few decoded frames into a tiny canvas. Most WebMs have no alpha channel
 * (and Safari decodes VP9 without it); only a transparent one is worth blending over the splats,
 * which costs the splat depth pass, so the plane stays on the opaque path until a transparent
 * pixel is actually seen.
 *
 * Call the returned function after each uploaded frame: 'alpha' once a transparent pixel is seen,
 * 'opaque' after VIDEO_ALPHA_PROBE_MAX_SAMPLES frames without one, 'pending' otherwise. When the
 * frame cannot be read back at all (no 2D context, a cross-origin / CORS-tainted video, a decode
 * error), the answer is 'opaque' and probing stops: the video keeps playing exactly as an opaque
 * video, and nothing throws into the render loop.
 */
import { imageDataHasAlpha } from './videoFormat';

/** Side of the canvas a video frame is drawn into to look for transparent pixels. */
export const VIDEO_ALPHA_PROBE_SIZE = 64;
/** Frames are sampled at most this often... */
export const VIDEO_ALPHA_PROBE_INTERVAL_MS = 500;
/** ...and at most this many times per video. */
export const VIDEO_ALPHA_PROBE_MAX_SAMPLES = 12;

export type VideoAlphaProbeResult = 'alpha' | 'pending' | 'opaque';

/** The part of a 2D canvas context the probe uses. */
export interface VideoAlphaProbeContext {
    clearRect(x: number, y: number, w: number, h: number): void;
    drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
    getImageData(sx: number, sy: number, sw: number, sh: number): { data: ArrayLike<number> };
}

/** Creates the probe's 2D context (null when the browser cannot). Injectable for tests. */
export type VideoAlphaProbeContextFactory = () => VideoAlphaProbeContext | null;

/** True when every pixel of RGBA data is fully transparent (alpha 0). */
export function isBlankFrame(data: ArrayLike<number>): boolean {
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return false;
    }
    return true;
}

function createProbeContext(): VideoAlphaProbeContext | null {
    if (typeof document === 'undefined') return null;
    const canvas = document.createElement('canvas');
    canvas.width = VIDEO_ALPHA_PROBE_SIZE;
    canvas.height = VIDEO_ALPHA_PROBE_SIZE;
    return canvas.getContext('2d', { willReadFrequently: true });
}

export function createVideoAlphaProbe(
    createContext: VideoAlphaProbeContextFactory = createProbeContext,
): (video: CanvasImageSource, now: number) => VideoAlphaProbeResult {
    let context: VideoAlphaProbeContext | null = null;
    let samples = 0;
    let lastSampleAt = -Infinity;
    let finished: VideoAlphaProbeResult | null = null;
    return (video, now) => {
        if (finished) return finished;
        if (now - lastSampleAt < VIDEO_ALPHA_PROBE_INTERVAL_MS) return 'pending';
        samples++;
        lastSampleAt = now;
        try {
            if (!context) context = createContext();
            if (!context) return (finished = 'opaque');
            context.clearRect(0, 0, VIDEO_ALPHA_PROBE_SIZE, VIDEO_ALPHA_PROBE_SIZE);
            context.drawImage(video, 0, 0, VIDEO_ALPHA_PROBE_SIZE, VIDEO_ALPHA_PROBE_SIZE);
            const pixels = context.getImageData(0, 0, VIDEO_ALPHA_PROBE_SIZE, VIDEO_ALPHA_PROBE_SIZE).data;
            // A frame that reads back fully transparent proves nothing (a draw that produced no
            // pixels looks the same): only a frame with some opaque pixels decides.
            if (!isBlankFrame(pixels) && imageDataHasAlpha(pixels)) return (finished = 'alpha');
        } catch {
            // SecurityError (tainted canvas), InvalidStateError, … : read back nothing, stay opaque.
            return (finished = 'opaque');
        }
        return samples >= VIDEO_ALPHA_PROBE_MAX_SAMPLES ? (finished = 'opaque') : 'pending';
    };
}
